import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ImageUp } from 'lucide-react';
import { toast } from 'sonner';
import { Badge, Button, Dialog, EmptyState, Field, Input, Spinner, Textarea } from '@zt/ui';
import { api, ApiError } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { Brand } from '../lib/types';

export function slugify(s: string): string {
  const map: Record<string, string> = {
    ç: 'c',
    ğ: 'g',
    ı: 'i',
    İ: 'i',
    ö: 'o',
    ş: 's',
    ü: 'u',
    â: 'a',
    î: 'i',
    û: 'u',
  };
  return s
    .toLocaleLowerCase('tr')
    .replace(/[çğıİöşüâîû]/g, (c) => map[c] ?? c)
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

const fileUrl = (id: string) => `/v1/files/${id}/content`;

function LogoUpload({ value, onChange }: { value: string | null; onChange: (id: string) => void }) {
  const t = useT();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  return (
    <div className="flex items-center gap-4">
      <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-md border border-line bg-surface-2">
        {value ? (
          <img src={fileUrl(value)} alt="" className="h-full w-full object-contain" />
        ) : (
          <span className="text-xs text-ink-3">{t('admin.brands.noLogo')}</span>
        )}
      </div>
      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/avif,image/gif"
        aria-label={value ? t('admin.brands.replaceLogo') : t('admin.brands.uploadLogo')}
        tabIndex={-1}
        className="sr-only"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          setBusy(true);
          try {
            const fd = new FormData();
            fd.append('file', file);
            const r = await api.upload<{ id: string }>('/v1/admin/files?purpose=brand_logo', fd);
            onChange(r.id);
            toast.success(t('admin.brands.logoUploaded'));
          } catch (err) {
            toast.error(err instanceof ApiError ? err.title : String(err));
          } finally {
            setBusy(false);
            if (input.current) input.current.value = '';
          }
        }}
      />
      <Button size="sm" loading={busy} onClick={() => input.current?.click()}>
        <ImageUp className="h-4 w-4" aria-hidden />
        {value ? t('admin.brands.replaceLogo') : t('admin.brands.uploadLogo')}
      </Button>
      <HelpTip k="brands.logo_upload" />
    </div>
  );
}

interface FormState {
  name: string;
  slug: string;
  description: string;
  color: string;
  logoFileId: string | null;
  slugTouched: boolean;
}

function BrandSheet({
  brand,
  open,
  onOpenChange,
}: {
  brand: Brand | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const t = useT();
  const [f, setF] = useState<FormState>(() => ({
    name: brand?.name ?? '',
    slug: brand?.slug ?? '',
    description: brand?.description ?? '',
    color: brand?.tokens?.primary ?? '#0d5a4e',
    logoFileId: brand?.logoFileId ?? null,
    slugTouched: Boolean(brand),
  }));
  const save = useApiMutation(
    () => {
      const body = {
        name: f.name,
        slug: f.slug,
        description: f.description || null,
        logoFileId: f.logoFileId,
        tokens: { ...(brand?.tokens ?? {}), primary: f.color },
      };
      return brand
        ? api.patch<Brand>(`/v1/admin/brands/${brand.id}`, body, { ifMatch: brand.version })
        : api.post<Brand>('/v1/admin/brands', body);
    },
    {
      invalidate: [['brands'], ['setup']],
      success: t('common.saved'),
      onSuccess: () => onOpenChange(false),
      silentFieldErrors: true,
    },
  );
  const err = (k: string) =>
    fieldError(save.error, k) ??
    (save.error?.code === 'slug_taken' && k === 'slug' ? save.error.title : undefined);
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      variant="sheet"
      title={brand ? t('admin.brands.editTitle') : t('admin.brands.createTitle')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(undefined)}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <Field label={t('admin.brands.name')} required error={err('name')}>
          {(p) => (
            <Input
              {...p}
              value={f.name}
              onChange={(e) =>
                setF({ ...f, name: e.target.value, slug: f.slugTouched ? f.slug : slugify(e.target.value) })
              }
            />
          )}
        </Field>
        <Field label={t('admin.brands.slug')} required error={err('slug')} help={<HelpTip k="brands.slug" />}>
          {(p) => (
            <Input
              {...p}
              value={f.slug}
              onChange={(e) => setF({ ...f, slug: e.target.value, slugTouched: true })}
            />
          )}
        </Field>
        <Field label={t('admin.brands.description')} optionalLabel={t('common.optional')}>
          {(p) => (
            <Textarea
              {...p}
              value={f.description}
              onChange={(e) => setF({ ...f, description: e.target.value })}
            />
          )}
        </Field>
        <Field label={t('admin.brands.color')} help={<HelpTip k="brands.primary_color" />}>
          {(p) => (
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={f.color}
                onChange={(e) => setF({ ...f, color: e.target.value })}
                className="h-10 w-14 cursor-pointer rounded-sm border border-line-strong bg-surface"
                aria-label={t('admin.brands.color')}
              />
              <Input
                {...p}
                value={f.color}
                onChange={(e) => setF({ ...f, color: e.target.value })}
                className="tabular w-32"
                maxLength={7}
              />
            </div>
          )}
        </Field>
        <div className="space-y-2">
          <p className="text-sm font-medium">{t('admin.brands.logo')}</p>
          <LogoUpload value={f.logoFileId} onChange={(id) => setF({ ...f, logoFileId: id })} />
        </div>
      </div>
    </Dialog>
  );
}

export function BrandsPage() {
  const t = useT();
  const { can } = usePermissions();
  const manage = can('org.brand.manage');
  const q = useQuery({ queryKey: ['brands'], queryFn: () => api.get<Brand[]>('/v1/admin/brands') });
  const [editing, setEditing] = useState<Brand | null>(null);
  const [open, setOpen] = useState(false);
  const toggle = useApiMutation(
    (b: Brand) =>
      api.patch(
        `/v1/admin/brands/${b.id}`,
        { status: b.status === 'active' ? 'archived' : 'active' },
        { ifMatch: b.version },
      ),
    {
      invalidate: [['brands']],
      success: t('common.saved'),
    },
  );

  const openSheet = (b: Brand | null) => {
    setEditing(b);
    setOpen(true);
  };

  return (
    <PageFrame
      helpKey="page.brands"
      actions={
        manage && (
          <div className="flex items-center gap-1">
            <Button variant="primary" onClick={() => openSheet(null)}>
              {t('admin.brands.add')}
            </Button>
            <HelpTip k="brands.create" />
          </div>
        )
      }
    >
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : q.data.length === 0 ? (
        <EmptyState
          title={t('admin.brands.emptyTitle')}
          description={t('admin.brands.emptyLead')}
          action={
            manage && (
              <Button variant="primary" onClick={() => openSheet(null)}>
                {t('admin.brands.add')}
              </Button>
            )
          }
        />
      ) : (
        <ul className="divide-y divide-line rounded-md border border-line bg-surface">
          {q.data.map((b) => (
            <li key={b.id} className="flex flex-wrap items-center gap-4 px-4 py-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-sm border border-line bg-surface-2">
                {b.logoFileId ? (
                  <img src={fileUrl(b.logoFileId)} alt="" className="h-full w-full object-contain" />
                ) : (
                  <span
                    className="h-5 w-5 rounded-full"
                    style={{ background: b.tokens?.primary ?? 'var(--zt-line)' }}
                  />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium">{b.name}</p>
                <p className="text-sm text-ink-3">/{b.slug}</p>
              </div>
              {b.tokens?.primary && (
                <span className="tabular inline-flex items-center gap-1.5 text-sm text-ink-2">
                  <span
                    className="h-3.5 w-3.5 rounded-full border border-line"
                    style={{ background: b.tokens.primary }}
                    aria-hidden
                  />
                  {b.tokens.primary}
                </span>
              )}
              <Badge tone={b.status === 'active' ? 'moss' : 'slate'}>
                {b.status === 'active' ? t('admin.brands.active') : t('admin.brands.archived')}
              </Badge>
              {manage && (
                <div className="flex gap-1">
                  <Button size="sm" onClick={() => openSheet(b)}>
                    {t('common.edit')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => toggle.mutate(b)}>
                    {b.status === 'active' ? t('admin.brands.archive') : t('admin.brands.activate')}
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {open && <BrandSheet key={editing?.id ?? 'new'} brand={editing} open={open} onOpenChange={setOpen} />}
    </PageFrame>
  );
}
